import { createInterfaceId, Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { doNotAwait } from '@gitlab-org/core';
import { DEFAULT_WELCOME_CATEGORIES } from '@gitlab-org/tui';
import type { WelcomePromptCategory } from '@gitlab-org/tui';
import { Logger, withPrefix } from '@gitlab-org/logging';
import {
  GetIssueRelatedMergeRequestsQuery,
  GetUserContextualWorkItemsQuery,
  GraphQLService,
} from '@gitlab-org/graphql';
import type {
  GetIssueRelatedMergeRequestsData,
  GetIssueRelatedMergeRequestsVariables,
  GetUserContextualWorkItemsData,
  GetUserContextualWorkItemsVariables,
} from '@gitlab-org/graphql';
import type { ControllerApi } from './controller_api';
import {
  buildIssueCategory,
  buildMergeRequestCategory,
  recentActivityCutoff,
  startPromptCandidateIssues,
} from './welcome_prompt_heuristics';

/**
 * Total budget for resolving contextual prompts, shared by all requests. When
 * exceeded, the card falls back to the generic defaults so the user is never
 * stuck watching a spinner longer than this.
 */
const FETCH_TIMEOUT_MS = 3000;

/**
 * `Issue.mergeRequestsCount` only covers MRs that close the issue, so the top
 * "start" candidates get an extra related-MR check. The GraphQL field resolves
 * one issue per request, so this caps the fan-out. Candidates beyond the cap
 * are unchecked and may rarely surface despite having a related MR.
 */
const MAX_LINKED_MR_CHECKS = 4;

export interface WelcomePromptsStartOptions {
  /** Full path of the project the CLI is running in, e.g. `group/project`. */
  projectPath: string;
  /** Username of the current user. */
  username: string;
}

export interface WelcomePromptsController {
  /**
   * Fetches the user's MRs and issues in the background and resolves
   * `AppState.welcomePrompts` exactly once: contextual prompt categories
   * padded with generics, or the plain defaults on failure/timeout. The
   * welcome card shows a loader until this resolves.
   */
  start(api: ControllerApi, options: WelcomePromptsStartOptions): void;
}

export const WelcomePromptsController = createInterfaceId<WelcomePromptsController>(
  'WelcomePromptsController',
);

@Implements(WelcomePromptsController)
@Service({
  dependencies: [Logger, GraphQLService],
  lifetime: ServiceLifetime.Singleton,
})
export class DefaultWelcomePromptsController implements WelcomePromptsController {
  #logger: Logger;

  #graphqlService: GraphQLService;

  constructor(logger: Logger, graphqlService: GraphQLService) {
    this.#logger = withPrefix(logger, '[WelcomePromptsController]');
    this.#graphqlService = graphqlService;
  }

  start(api: ControllerApi, options: WelcomePromptsStartOptions): void {
    doNotAwait(this.#load(api, options));
  }

  async #load(api: ControllerApi, options: WelcomePromptsStartOptions): Promise<void> {
    let welcomePrompts: WelcomePromptCategory[] = DEFAULT_WELCOME_CATEGORIES;
    const signal = AbortSignal.timeout(FETCH_TIMEOUT_MS);
    try {
      const data = await this.#graphqlService.execute<
        GetUserContextualWorkItemsData,
        GetUserContextualWorkItemsVariables
      >(
        GetUserContextualWorkItemsQuery,
        {
          projectPath: options.projectPath,
          username: options.username,
          updatedAfter: recentActivityCutoff(),
        },
        signal,
      );

      const issuesWithLinkedMrs = await this.#findIssuesWithLinkedMrs(
        options.projectPath,
        startPromptCandidateIssues(data).slice(0, MAX_LINKED_MR_CHECKS),
        signal,
      );
      welcomePrompts = [
        buildMergeRequestCategory(data),
        buildIssueCategory(data, issuesWithLinkedMrs),
      ];

      const summary = {
        project: data.project?.id ?? null,
        authored: data.project?.authoredMergeRequests?.nodes?.length ?? 0,
        assigned: data.project?.assignedMergeRequests?.nodes?.length ?? 0,
        issues: data.project?.issues?.nodes?.length ?? 0,
        issuesWithLinkedMrs: [...issuesWithLinkedMrs],
      };
      this.#logger.debug(`Welcome prompts resolved. Data: ${JSON.stringify(summary)}`);
    } catch (error) {
      this.#logger.debug(
        'Failed to load contextual welcome prompts; using generic defaults',
        error instanceof Error ? error : new Error(String(error)),
      );
    }
    api.mutateState((state) => ({ ...state, welcomePrompts }));
  }

  /**
   * Checks the candidate issues for related (linked) merge requests, one
   * request per issue (server-side limit), in parallel. A failed, aborted, or
   * unsupported check treats the issue as having no linked MRs so the prompt
   * degrades to the closing-MR-only heuristic instead of disappearing.
   */
  async #findIssuesWithLinkedMrs(
    projectPath: string,
    candidates: { iid: string }[],
    signal: AbortSignal,
  ): Promise<Set<string>> {
    const results = await Promise.all(
      candidates.map(async ({ iid }) => {
        try {
          const data = await this.#graphqlService.execute<
            GetIssueRelatedMergeRequestsData,
            GetIssueRelatedMergeRequestsVariables
          >(GetIssueRelatedMergeRequestsQuery, { projectPath, iid }, signal);
          const hasLinkedMrs = (data.project?.issue?.relatedMergeRequests?.nodes?.length ?? 0) > 0;
          return hasLinkedMrs ? iid : undefined;
        } catch (error) {
          this.#logger.debug(
            `Related MR check failed for issue #${iid}; assuming none`,
            error instanceof Error ? error : new Error(String(error)),
          );
          return undefined;
        }
      }),
    );
    return new Set(results.filter((iid): iid is string => iid !== undefined));
  }
}
